/**
 * Till sale of one meal-plan week. The subscription is written only after the POS order is paid.
 */

import type { MealPlanSale, MealPlanSubscription } from '../types';
import {
  autoBookSubscriptionForWeek,
  mealPlanSubscriptionsService,
  mealPlanTypesService,
  mealPlanWeeksService,
  weeklyBookingsService,
} from './database';
import { weekBoundsMs } from './mealPlanWeek';
import {
  customerHasPlanForWeek,
  posMealPlanSubscriptionId,
} from './mealPlanSaleRules';

export {
  customerAlreadyOnWeek,
  customerHasPlanForWeek,
  mealPlanSaleLine,
  mealPlanSalePriceDollars,
  posMealPlanSubscriptionId,
  subscriptionCoversWeek,
} from './mealPlanSaleRules';

/**
 * Create the one-week subscription and bookings for a fully paid till sale.
 * Safe to call again for the same order.
 */
export async function activatePaidMealPlanSale(params: {
  orderId: string;
  sale: MealPlanSale;
  staffId?: number | string;
  subscriptionId?: string;
}): Promise<MealPlanSale> {
  const week = await mealPlanWeeksService.getById(params.sale.weekId);
  if (!week || (week.status !== 'published' && week.status !== 'locked')) {
    throw new Error('Next week’s menu is not published.');
  }

  const subId = params.subscriptionId || posMealPlanSubscriptionId(params.orderId);
  const existing = await mealPlanSubscriptionsService.getById(subId);
  if (existing) {
    return {
      ...params.sale,
      subscriptionId: existing.id,
      activatedAt: params.sale.activatedAt || existing.createdAt,
    };
  }

  const others = await mealPlanSubscriptionsService.getByCustomerId(params.sale.customerId);
  if (customerHasPlanForWeek(others, params.sale.customerId, params.sale.weekId, params.sale.planTypeId)) {
    throw new Error('This customer already has that plan for the week.');
  }

  const plan =
    (await mealPlanTypesService.getAll()).find((t) => t.id === params.sale.planTypeId) || null;
  const now = Date.now();
  const { weekEndMs } = weekBoundsMs(params.sale.weekId);
  const sub = await mealPlanSubscriptionsService.save({
    id: subId,
    customerId: String(params.sale.customerId),
    customerEmail: (params.sale.customerEmail || '').trim().toLowerCase(),
    planTypeId: params.sale.planTypeId,
    planTypeName: params.sale.planTypeName,
    status: 'active',
    activeFrom: now,
    activeTo: weekEndMs,
    coveredWeekId: params.sale.weekId,
    paymentSource: 'pos',
    posOrderId: params.orderId,
    createdAt: now,
    updatedAt: now,
    createdByStaffId: params.staffId,
  });

  if (plan && plan.bookingMode !== 'manual') {
    await autoBookSubscriptionForWeek(sub, plan, params.sale.weekId);
  }

  return { ...params.sale, subscriptionId: sub.id, activatedAt: now };
}

/** Activate every plan on one till charge. One plan keeps the original subscription id. */
export async function activatePaidMealPlanOrder(params: {
  orderId: string;
  sales: MealPlanSale[];
  staffId?: number | string;
}): Promise<{ mealPlanSale?: MealPlanSale; mealPlanSales?: MealPlanSale[] }> {
  const activated: MealPlanSale[] = [];
  for (const sale of params.sales) {
    if (sale.activatedAt && sale.subscriptionId) {
      activated.push(sale);
      continue;
    }
    const subscriptionId = params.sales.length === 1
      ? posMealPlanSubscriptionId(params.orderId)
      : `${posMealPlanSubscriptionId(params.orderId)}-${sale.planTypeId}`;
    activated.push(await activatePaidMealPlanSale({
      orderId: params.orderId,
      sale,
      staffId: params.staffId,
      subscriptionId,
    }));
  }
  return {
    mealPlanSale: activated[0],
    ...(activated.length > 1 ? { mealPlanSales: activated } : {}),
  };
}

/** Drop still-booked meals for the covered week and mark the subscription cancelled. */
export async function cancelMealPlanLocally(
  sub: MealPlanSubscription,
  beforeCutoff: boolean,
): Promise<void> {
  if (beforeCutoff && sub.coveredWeekId) {
    const rows = await weeklyBookingsService.getByWeekId(sub.coveredWeekId);
    const now = Date.now();
    for (const booking of rows) {
      if (booking.status !== 'booked') continue;
      if (String(booking.customerId) !== String(sub.customerId)) continue;
      if (booking.subscriptionId && booking.subscriptionId !== sub.id) continue;
      await weeklyBookingsService.save({
        ...booking,
        status: 'cancelled',
        cancelledAt: now,
        updatedAt: now,
      });
    }
  }
  const now = Date.now();
  await mealPlanSubscriptionsService.save({
    ...sub,
    status: 'cancelled',
    cancelledAt: sub.cancelledAt || now,
    updatedAt: now,
  });
}
